import { useMemo, type ReactNode } from "react";
import { MarkdownReact } from "@/components/MarkdownReact";
import {
  formatQuoteLineTotal,
  formatQuoteUnitPrice,
} from "@/lib/quoteCurrency";
import {
  WEIGHT_UNIT_ABBREVIATION,
  type PublicQuote,
  type PublicQuoteItemLine,
  type PublicQuoteLine,
} from "@forgepoint/shared";
import { formatDateOnly } from "@forgepoint/shared";
import {
  computeLineNumbers,
  formatAddressLines,
  getQuoteEffectiveDate,
  getTotalWeightsByUnit,
} from "../utils";
import { FitToPageContent } from "../components/FitToPageContent";
import {
  PdfPageViewer,
  usePdfPageInfo,
  type QuoteSection,
} from "../components/PdfPageViewer";

interface PublicQuoteTemplateServiceQuoteProps {
  quote: PublicQuote;
  isExpired: boolean;
}

const DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  month: "long",
  day: "numeric",
  year: "numeric",
};

const formatDate = (value?: Date | string | null) => {
  if (!value) {
    return null;
  }
  if (typeof value === "string") {
    return formatDateOnly(value, DATE_FORMAT_OPTIONS, "en-US");
  }
  if (Number.isNaN(value.getTime())) {
    return null;
  }
  return value.toLocaleDateString("en-US", DATE_FORMAT_OPTIONS);
};

type PublicQuoteDiscountLine = Extract<PublicQuoteLine, { type: "DISCOUNT" }>;

type PricedTableLine = PublicQuoteItemLine | PublicQuoteDiscountLine;

interface ServiceQuoteLineSection {
  key: string;
  title: string;
  lines: PublicQuoteLine[];
}

interface IndexedServiceQuoteLineSection {
  index: number;
  lineSection: ServiceQuoteLineSection;
}

const FALLBACK_SECTION_TITLE = "Quote Details";
const COVER_LETTER_MAX_HEIGHT = 876;

/**
 * Groups ordered quote lines into generic service quote document sections.
 * SECTION lines are top-level page carve-outs; following lines belong to that
 * section until the next SECTION. Older quotes without SECTION lines fall back
 * to one generic section so their content still renders.
 */
function buildServiceQuoteLineSections(
  lines: PublicQuoteLine[],
): ServiceQuoteLineSection[] {
  const result: ServiceQuoteLineSection[] = [];
  let currentSection: ServiceQuoteLineSection | null = null;

  const flushCurrentSection = () => {
    if (currentSection) {
      result.push(currentSection);
      currentSection = null;
    }
  };

  lines.forEach((line, index) => {
    if (line.type === "SECTION") {
      flushCurrentSection();
      currentSection = {
        key: `section-${line.id || index}`,
        title: line.description?.trim() || `Section ${result.length + 1}`,
        lines: [],
      };
      return;
    }

    if (!currentSection) {
      currentSection = {
        key: "section-fallback",
        title: FALLBACK_SECTION_TITLE,
        lines: [],
      };
    }

    currentSection.lines.push(line);
  });

  flushCurrentSection();
  return result;
}

function groupServiceQuoteLineSections(
  lineSections: ServiceQuoteLineSection[],
): IndexedServiceQuoteLineSection[][] {
  return lineSections.reduce<IndexedServiceQuoteLineSection[][]>(
    (groups, lineSection, index) => {
      const entry = { index, lineSection };
      if (
        index === 0 ||
        lineSection.lines.some((line) => line.type === "ITEM")
      ) {
        groups.push([entry]);
      } else {
        groups.at(-1)?.push(entry);
      }
      return groups;
    },
    [],
  );
}

/**
 * Formats an item line's cost estimate rows from its quantity breaks.
 * @param line - The hydrated item line
 * @param quote - The hydrated quote data (for display-currency formatting)
 * @returns Array of formatted cost estimate objects
 */
function formatItemCostEstimates(
  line: PublicQuoteItemLine,
  quote: PublicQuote,
) {
  return line.quantityBreaks
    .map((qb) => {
      const quantity = qb.quantity?.trim();
      if (!quantity) {
        return null;
      }
      const unitPrice = qb.unitPriceOverride ?? qb.unitPrice;
      if (!unitPrice) {
        return null;
      }
      return {
        quantity,
        unitPrice: formatQuoteUnitPrice(qb, quote),
        totalPrice: formatQuoteLineTotal(qb, quote),
      };
    })
    .filter(
      (
        entry,
      ): entry is { quantity: string; unitPrice: string; totalPrice: string } =>
        entry !== null,
    );
}

/**
 * Ensures a quote display number is prefixed with "EP-".
 * Returns null if the input is falsy.
 * @param displayNumber - The raw quote display number
 * @returns The prefixed display number, or null
 */
function formatQuoteDisplayNumber(
  displayNumber: string | null | undefined,
): string | null {
  if (!displayNumber) {
    return null;
  }
  if (displayNumber.startsWith("EP-")) {
    return displayNumber;
  }
  return `EP-${displayNumber}`;
}

/**
 * Looks up a custom field value by key, ignoring case differences.
 * @param customFields - The quote's custom fields map
 * @param key - The field name to look up (case-insensitive)
 * @returns The field value, or undefined if not found
 */
function getCustomFieldCaseInsensitive(
  customFields: Record<string, unknown> | null | undefined,
  key: string,
): unknown {
  if (!customFields) {
    return undefined;
  }
  const lowerKey = key.toLowerCase();
  const matchingKey = Object.keys(customFields).find(
    (k) => k.toLowerCase() === lowerKey,
  );
  return matchingKey ? customFields[matchingKey] : undefined;
}

const SYTELINE_QUOTE_NUMBER_FIELD = "Syteline Quote Number";

/**
 * Resolves the customer-facing quote number, preferring the Syteline Quote Number
 * custom field when present, falling back to the ForgePoint display number.
 * @param quote - The hydrated quote data
 * @returns The formatted quote number, or null
 */
function resolveQuoteDisplayNumber(quote: PublicQuote): string | null {
  const sytelineValue =
    getCustomFieldCaseInsensitive(
      quote.customFields,
      SYTELINE_QUOTE_NUMBER_FIELD,
    ) ?? null;
  if (typeof sytelineValue === "string" && sytelineValue.trim()) {
    return sytelineValue.trim();
  }
  return formatQuoteDisplayNumber(quote.quoteDisplayNumber);
}

function cleanCustomerFacingItemDescription(
  description: string | null | undefined,
  fallbackName: string | null | undefined,
): string {
  const rawDescription = description?.trim() || fallbackName?.trim() || "Item";
  const cleanedDescription = rawDescription
    .replace(/^EP-\d+[A-Z]?\s*[-–—:]\s*/i, "")
    .replace(/^EP-\d+[A-Z]?\s+/i, "")
    .trim();

  return cleanedDescription || rawDescription;
}

/**
 * Service Quote public quote template.
 * Renders a proposal-style document with cover page, cover letter, generic
 * quote-line sections, pricing tables, markdown notes, and terms.
 * @param quote - The hydrated quote data
 * @param isExpired - Whether the quote has expired
 */
export function PublicQuoteTemplateServiceQuote({
  quote,
}: PublicQuoteTemplateServiceQuoteProps) {
  const { sections, repeatableFooter } = useServiceQuoteSections(quote);
  return (
    <PdfPageViewer
      sections={sections}
      repeatableFooter={repeatableFooter}
      useContentAwarePageBreaks
      watermark={quote.watermark}
    />
  );
}

interface ServiceQuoteLayout {
  sections: QuoteSection[];
  repeatableFooter: ReactNode;
}

/**
 * Builds the pageable sections and repeatable footer for the Service Quote layout.
 * @param quote - The hydrated quote data
 * @returns Sections and a repeatable footer for PdfPageViewer
 */
function useServiceQuoteSections(quote: PublicQuote): ServiceQuoteLayout {
  const effectiveDate = formatDate(getQuoteEffectiveDate(quote));
  const customerName = quote.customerCompany?.name ?? null;
  const orgAddressLines = formatAddressLines(quote.organization?.address);
  const lines = useMemo(() => quote.lines ?? [], [quote.lines]);

  const lineSections = useMemo(
    () => buildServiceQuoteLineSections(lines),
    [lines],
  );
  const lineSectionGroups = useMemo(
    () => groupServiceQuoteLineSections(lineSections),
    [lineSections],
  );

  const lineSequenceNumbers = useMemo(() => computeLineNumbers(lines), [lines]);

  const totalWeightsByUnit = useMemo(
    () => getTotalWeightsByUnit(lines),
    [lines],
  );

  const lastPricedSectionIndex = useMemo(
    () =>
      lineSections.reduce(
        (lastIndex, lineSection, index) =>
          lineSection.lines.some((line) => line.type === "ITEM")
            ? index
            : lastIndex,
        -1,
      ),
    [lineSections],
  );

  // Pages before content (cover + optional cover letter) are excluded from
  // the footer page counter so the first content page displays "Page | 1".
  const footerPageOffset = 1;

  const repeatableFooter = (
    <ServiceQuoteFooter quote={quote} pageOffset={footerPageOffset} />
  );

  const sections: QuoteSection[] = [];

  // --- Cover page ---
  sections.push({
    key: "cover-page",
    forcePageBreak: false,
    skipHeader: true,
    skipFooter: true,
    content: (
      <CoverPage
        quote={quote}
        customerName={customerName}
        effectiveDate={effectiveDate}
        orgAddressLines={orgAddressLines}
      />
    ),
  });

  // --- Cover letter ---
  if (quote.enableCoverLetter && quote.coverLetterMarkdown) {
    sections.push({
      key: "cover-letter",
      forcePageBreak: true,
      skipHeader: true,
      content: (
        <FitToPageContent baseFontSize={12} maxHeight={COVER_LETTER_MAX_HEIGHT}>
          <div
            className="prose prose-slate max-w-none text-gray-800"
            style={{ fontSize: "inherit", lineHeight: 1.5 }}
          >
            {quote.organization?.logoUrl && (
              <img
                src={quote.organization.logoUrl}
                alt={`${quote.organization.name} logo`}
                className="h-12 w-auto object-contain not-prose mb-4"
              />
            )}
            <MarkdownReact>{quote.coverLetterMarkdown}</MarkdownReact>
          </div>
        </FitToPageContent>
      ),
    });
  }

  lineSectionGroups.forEach((lineSectionGroup) => {
    sections.push({
      key: lineSectionGroup.map(({ lineSection }) => lineSection.key).join("-"),
      forcePageBreak: true,
      content: (
        <div>
          {lineSectionGroup.map(({ index, lineSection }) => (
            <ServiceQuoteLineSectionContent
              allLines={lines}
              customerName={customerName}
              includeIntro={index === 0}
              includeTotalWeights={
                index === lastPricedSectionIndex &&
                quote.showTotals &&
                (quote.showTotalWeight ?? false)
              }
              key={lineSection.key}
              lineSection={lineSection}
              lineSequenceNumbers={lineSequenceNumbers}
              quote={quote}
              totalWeightsByUnit={totalWeightsByUnit}
            />
          ))}
        </div>
      ),
    });
  });

  // --- Commercial Terms (location, payment, validity, delivery, T&C) ---
  const formattedExpirationDate = formatDate(quote.expirationDate);
  const validityText = formattedExpirationDate
    ? `Pricing in this quotation may be considered valid until ${formattedExpirationDate}.`
    : "N/A";
  const fabricationLocationName = quote.location?.name?.trim() || null;
  sections.push({
    key: "commercial-terms",
    forcePageBreak: true,
    skipHeader: true,
    content: (
      <div>
        <div className="mb-4">
          <p
            className="font-bold text-[#c00000]"
            style={{ fontFamily: "Arial, sans-serif", fontSize: "16pt" }}
          >
            Commercial Terms
          </p>
          <hr className="border-t border-black" />
        </div>
        {fabricationLocationName && (
          <div className="mb-4">
            <h3 className="text-xs font-bold text-gray-900 underline mb-1">
              Fabrication Location
            </h3>
            <p className="text-xs text-gray-800">{fabricationLocationName}</p>
          </div>
        )}
        {quote.paymentTerms && (
          <div className="mb-4">
            <h3 className="text-xs font-bold text-gray-900 underline mb-1">
              Payment Terms
            </h3>
            <div className="prose prose-xs prose-slate max-w-none text-gray-800 text-xs [&_li::marker]:text-black [&_ul_ul]:list-[circle]">
              <MarkdownReact>{quote.paymentTerms}</MarkdownReact>
            </div>
          </div>
        )}
        <div className="mb-4">
          <h3 className="text-xs font-bold text-gray-900 underline mb-1">
            Validity
          </h3>
          <p className="text-xs text-gray-800">{validityText}</p>
        </div>
        {quote.deliveryTerms && (
          <div className="mb-4">
            <h3 className="text-xs font-bold text-gray-900 underline mb-1">
              Delivery Terms
            </h3>
            <div className="prose prose-xs prose-slate max-w-none text-gray-800 text-xs [&_li::marker]:text-black [&_ul_ul]:list-[circle]">
              <MarkdownReact>{quote.deliveryTerms}</MarkdownReact>
            </div>
          </div>
        )}
        {quote.quoteFooter && (
          <div className="mt-4 pt-2 text-xs text-gray-800 whitespace-pre-wrap">
            {quote.quoteFooter}
          </div>
        )}
        {quote.termsOfService && (
          <div className="mb-4">
            <h3 className="text-xs font-bold text-gray-900 underline mb-1">
              Terms and Conditions
            </h3>
            <p className="text-xs text-gray-800 mb-3">
              {quote.organization?.name} standard terms and conditions shall
              apply for this offer. A copy of these terms and conditions is
              enclosed.
            </p>
          </div>
        )}
      </div>
    ),
  });

  // --- Full Terms and Conditions body (flows across pages) ---
  if (quote.termsOfService) {
    sections.push({
      key: "terms",
      forcePageBreak: true,
      skipHeader: true,
      skipFooter: true,
      content: (
        <FitToPageContent>
          {quote.organization?.logoUrl && (
            <div className="flex justify-center mb-4">
              <img
                src={quote.organization.logoUrl}
                alt={`${quote.organization.name} logo`}
                className="h-12 w-auto object-contain"
              />
            </div>
          )}
          <h2
            className="font-bold uppercase text-gray-900 mb-3 text-center border-b border-gray-300 pb-2"
            style={{ fontSize: "1.55em" }}
          >
            {quote.organization?.name} Terms and Conditions
          </h2>
          <div
            className="prose prose-xs prose-slate max-w-none text-gray-800 leading-tight columns-2 gap-6"
            style={{ fontSize: "inherit" }}
          >
            <MarkdownReact>{quote.termsOfService}</MarkdownReact>
          </div>
        </FitToPageContent>
      ),
    });
  }

  // --- Signature (already-signed quotes) ---
  if (quote.isSigned && quote.signature) {
    sections.push({
      key: "signature",
      content: (
        <div className="mt-4 border-t border-gray-300 pt-4">
          <table className="border-collapse text-xs">
            <tbody>
              <tr>
                <td className="border border-gray-400 px-2 py-1 font-bold text-gray-900 bg-gray-50">
                  Authorized Signature
                </td>
                <td className="border border-gray-400 px-2 py-1">
                  <span className="font-signature text-xl text-gray-900">
                    {quote.signature.name}
                  </span>
                </td>
              </tr>
              <tr>
                <td className="border border-gray-400 px-2 py-1 font-bold text-gray-900 bg-gray-50">
                  Date Signed
                </td>
                <td className="border border-gray-400 px-2 py-1 text-gray-900">
                  {new Date(quote.signature.signedAt).toLocaleDateString()}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      ),
    });
  }

  return { sections, repeatableFooter };
}

function ServiceQuoteLineSectionContent({
  allLines,
  customerName,
  includeIntro,
  includeTotalWeights,
  lineSection,
  lineSequenceNumbers,
  quote,
  totalWeightsByUnit,
}: {
  allLines: PublicQuoteLine[];
  customerName: string | null;
  includeIntro: boolean;
  includeTotalWeights: boolean;
  lineSection: ServiceQuoteLineSection;
  lineSequenceNumbers: (number | null)[];
  quote: PublicQuote;
  totalWeightsByUnit: ReturnType<typeof getTotalWeightsByUnit>;
}) {
  const elements: ReactNode[] = [];
  let tableLines: PricedTableLine[] = [];
  let tableIndex = 0;

  const hasLeadTime = allLines.some((line) => resolveLineLeadTime(line));

  const flushTableLines = () => {
    if (tableLines.length === 0) {
      return;
    }
    elements.push(
      <PricedLinesTable
        allLines={allLines}
        hasLeadTime={hasLeadTime}
        key={`priced-table-${tableIndex}`}
        lineSequenceNumbers={lineSequenceNumbers}
        quote={quote}
        tableLines={tableLines}
      />,
    );
    tableLines = [];
    tableIndex += 1;
  };

  if (includeIntro) {
    elements.push(
      <ProposalIntro
        customerName={customerName}
        key="proposal-intro"
        quote={quote}
      />,
    );
  }

  elements.push(
    <h2
      key="section-heading"
      className="text-sm font-bold text-gray-900 mt-6 mb-3"
    >
      {lineSection.title}
    </h2>,
  );

  lineSection.lines.forEach((line, index) => {
    if (line.type === "ITEM") {
      tableLines.push(line);
      return;
    }

    if (line.type === "DISCOUNT") {
      if (quote.showTotals) {
        tableLines.push(line);
      }
      return;
    }

    if (line.type === "NOTE" && line.description) {
      flushTableLines();
      elements.push(
        <MarkdownNoteBlock
          key={`note-${line.id || index}`}
          markdown={line.description}
        />,
      );
    }
  });

  flushTableLines();

  if (includeTotalWeights && totalWeightsByUnit.length > 0) {
    elements.push(
      <TotalWeightRows
        hasLeadTime={hasLeadTime}
        key="total-weights"
        totalWeightsByUnit={totalWeightsByUnit}
      />,
    );
  }

  return <div>{elements}</div>;
}

function ProposalIntro({
  customerName,
  quote,
}: {
  customerName: string | null;
  quote: PublicQuote;
}) {
  return (
    <div className="mb-6">
      <QuoteNumberHeader quote={quote} />
      <p className="text-xs text-gray-700 mb-2">
        <span className="font-semibold">{quote.organization?.name}</span> is
        pleased to provide pricing to fabricate the following{" "}
        <span className="font-semibold italic">
          {quote.name || "scope of work"}
        </span>{" "}
        as required by{" "}
        <span className="font-semibold">{customerName ?? "the client"}</span>{" "}
        and described below.
      </p>
    </div>
  );
}

const MARKDOWN_NOTE_BASE_CLASS = [
  "prose prose-xs prose-slate max-w-none text-gray-800 text-xs my-4 pl-5",
  "[&_li::marker]:text-black",
  "[&_p:first-child]:mt-0 [&_p:last-child]:mb-0",
  "[&_ul:first-child]:mt-0 [&_ul:last-child]:mb-0",
  "[&_ol:first-child]:mt-0 [&_ol:last-child]:mb-0",
  "[&_ul_ul]:list-[circle]",
  "[&_h1]:text-sm [&_h1]:font-bold [&_h1]:mt-3 [&_h1]:mb-1 [&_h1]:ml-0",
  "[&_h2]:text-sm [&_h2]:font-bold [&_h2]:mt-3 [&_h2]:mb-1 [&_h2]:ml-4",
  "[&_h3]:text-xs [&_h3]:font-bold [&_h3]:mt-3 [&_h3]:mb-1 [&_h3]:ml-8",
  "[&_h4]:text-xs [&_h4]:font-bold [&_h4]:mt-3 [&_h4]:mb-1 [&_h4]:ml-12",
  "[&_h5]:text-xs [&_h5]:font-bold [&_h5]:mt-3 [&_h5]:mb-1 [&_h5]:ml-12",
  "[&_h6]:text-xs [&_h6]:font-bold [&_h6]:mt-3 [&_h6]:mb-1 [&_h6]:ml-12",
].join(" ");

const MARKDOWN_NOTE_HEADING_BODY_CLASS =
  "[&_p]:ml-12 [&_ul]:ml-12 [&_ol]:ml-12";

function MarkdownNoteBlock({ markdown }: { markdown: string }) {
  const hasHeadings = /^#{1,6}\s/m.test(markdown);
  const className = hasHeadings
    ? `${MARKDOWN_NOTE_BASE_CLASS} ${MARKDOWN_NOTE_HEADING_BODY_CLASS}`
    : MARKDOWN_NOTE_BASE_CLASS;

  return (
    <div className={className}>
      <MarkdownReact>{markdown}</MarkdownReact>
    </div>
  );
}

const DISPLAY_NUMBER_FIELD = "Display Line Number";
const LEAD_TIME_FIELD = "Lead Time";

/**
 * Resolves a line's custom field as a non-empty trimmed string, matched
 * case-insensitively. Only ITEM lines carry custom fields, so non-item lines
 * always return null.
 * @param line - The hydrated quote line
 * @param fieldName - The custom field name to match case-insensitively
 * @returns The trimmed field value, or null if not set
 */
function resolveLineCustomString(
  line: PublicQuoteLine,
  fieldName: string,
): string | null {
  if (line.type !== "ITEM") {
    return null;
  }
  const value = getCustomFieldCaseInsensitive(line.customFields, fieldName);
  if (typeof value === "string" && value.trim()) {
    return value.trim();
  }
  return null;
}

/**
 * Resolves a line's display-number override (e.g. "1a"), if configured.
 * @param line - The hydrated quote line
 * @returns The display number string, or null
 */
function resolveLineDisplayNumber(line: PublicQuoteLine): string | null {
  return resolveLineCustomString(line, DISPLAY_NUMBER_FIELD);
}

/**
 * Resolves a line's lead-time value (e.g. "6 weeks"), if configured.
 * @param line - The hydrated quote line
 * @returns The lead-time string, or null
 */
function resolveLineLeadTime(line: PublicQuoteLine): string | null {
  return resolveLineCustomString(line, LEAD_TIME_FIELD);
}

function PricedLinesTable({
  allLines,
  hasLeadTime,
  lineSequenceNumbers,
  quote,
  tableLines,
}: {
  allLines: PublicQuoteLine[];
  hasLeadTime: boolean;
  lineSequenceNumbers: (number | null)[];
  quote: PublicQuote;
  tableLines: PricedTableLine[];
}) {
  return (
    <table
      className={`${COMMERCIAL_TABLE_CLASS} my-4`}
      data-pdf-keep-together="true"
    >
      {buildCommercialColgroup(hasLeadTime)}
      <thead>
        <tr>
          <th className={TABLE_HEADER_CELL_CLASS}>Item</th>
          <th className={TABLE_HEADER_CELL_CLASS}>Description</th>
          <th className={TABLE_HEADER_CELL_CLASS}>Qty</th>
          <th className={TABLE_HEADER_CELL_CLASS}>Net Price (Each)</th>
          <th className={TABLE_HEADER_CELL_CLASS}>Net Price (Total)</th>
          {hasLeadTime && (
            <th className={TABLE_HEADER_CELL_CLASS}>Lead Time</th>
          )}
        </tr>
      </thead>
      <tbody>
        {tableLines.map((line, index) => {
          const globalIndex = allLines.indexOf(line);
          if (line.type === "ITEM") {
            return renderItemRows(
              line,
              globalIndex === -1 ? index : globalIndex,
              lineSequenceNumbers[globalIndex] ?? null,
              quote,
              hasLeadTime,
            );
          }

          return renderDiscountRow(
            line,
            quote,
            `discount-${line.id || index}`,
            hasLeadTime,
          );
        })}
      </tbody>
    </table>
  );
}

function renderDiscountRow(
  line: PublicQuoteDiscountLine,
  quote: PublicQuote,
  key: string,
  hasLeadTime: boolean,
) {
  const discount =
    line.amount !== null ? formatQuoteLineTotal(line, quote) : null;

  return (
    <tr key={key}>
      <td
        colSpan={hasLeadTime ? 5 : 4}
        className={`${TABLE_CELL_CLASS} font-semibold`}
      >
        {line.description || "Discount"}
      </td>
      <td
        className={`${TABLE_CELL_CLASS} text-right font-semibold whitespace-nowrap`}
      >
        {discount ? `-${discount}` : ""}
      </td>
    </tr>
  );
}

function TotalWeightRows({
  hasLeadTime,
  totalWeightsByUnit,
}: {
  hasLeadTime: boolean;
  totalWeightsByUnit: ReturnType<typeof getTotalWeightsByUnit>;
}) {
  return (
    <table className={`${COMMERCIAL_TABLE_CLASS} mt-2`}>
      {buildCommercialColgroup(hasLeadTime)}
      <tbody>
        {totalWeightsByUnit.map(([unit, weight]) => (
          <tr key={unit}>
            <td
              colSpan={hasLeadTime ? 5 : 4}
              className={`${TABLE_CELL_CLASS} text-right font-semibold`}
            >
              Total Weight (
              {
                WEIGHT_UNIT_ABBREVIATION[
                  unit as keyof typeof WEIGHT_UNIT_ABBREVIATION
                ]
              }
              )
            </td>
            <td className={`${TABLE_CELL_CLASS} text-right`}>
              {weight.toFixed(2)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * Cover page section — org branding, customer name, project title, and date.
 * Fills the entire first page with centered content.
 */
function CoverPage({
  quote,
  customerName,
  effectiveDate,
  orgAddressLines,
}: {
  quote: PublicQuote;
  customerName: string | null;
  effectiveDate: string | null;
  orgAddressLines: string[] | null;
}) {
  return (
    <div className="flex flex-col items-center justify-between min-h-[800px] text-center">
      <div className="flex-1" />

      {/* Organization branding */}
      {quote.organization?.logoUrl && (
        <img
          src={quote.organization.logoUrl}
          alt={`${quote.organization.name} logo`}
          className="h-28 w-auto object-contain mx-auto mb-48"
        />
      )}

      {/* Project details */}
      <div className="space-y-1">
        {customerName && (
          <p className="text-3xl font-bold text-black">{customerName}</p>
        )}
        {quote.name && (
          <p className="text-2xl font-bold text-black">{quote.name}</p>
        )}
        {resolveQuoteDisplayNumber(quote) && (
          <p className="text-xl font-bold text-black">
            Request for Proposal Number: {resolveQuoteDisplayNumber(quote)}
          </p>
        )}
        {effectiveDate && (
          <p className="text-xl font-bold text-black">{effectiveDate}</p>
        )}
      </div>

      <div className="flex-1" />

      {/* Organization footer */}
      <div className="text-center space-y-3 pb-4">
        {orgAddressLines && (
          <p className="text-lg font-bold italic text-black">
            {quote.organization?.name} Headquarters:{" "}
            {orgAddressLines.join(", ")}
          </p>
        )}
        {(quote.organization?.website || quote.organization?.phone) && (
          <p
            className="text-lg font-bold italic tracking-[0.25em] text-black"
            style={{ transform: "skewX(-10deg)" }}
          >
            {[quote.organization?.website, quote.organization?.phone]
              .filter(Boolean)
              .join("   |   ")}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Displays the quote number in red with a divider line below.
 * Shown at the top of the first content page.
 * @param quote - The hydrated quote data
 */
function QuoteNumberHeader({ quote }: { quote: PublicQuote }) {
  const formatted = resolveQuoteDisplayNumber(quote);
  if (!formatted) {
    return null;
  }
  return (
    <div className="mb-4">
      <p
        className="font-bold text-[#c00000]"
        style={{ fontFamily: "Arial, sans-serif", fontSize: "16pt" }}
      >
        {formatted}
      </p>
      <hr className="border-t border-black" />
    </div>
  );
}

/**
 * Repeatable footer shown at the bottom of content pages.
 * Displays a divider, page number, org logo, and confidentiality notice.
 * @param quote - The hydrated quote data
 * @param pageOffset - Number of pages before content starts (cover + cover letter)
 */
function ServiceQuoteFooter({
  quote,
  pageOffset,
}: {
  quote: PublicQuote;
  pageOffset: number;
}) {
  const { pageNumber } = usePdfPageInfo();
  const displayPage = pageNumber - pageOffset;

  return (
    <div className="pt-2">
      <hr className="border-t border-gray-300" />
      <p className="text-right text-xs text-gray-600 mt-1">
        <span className="tracking-[0.3em]">Page</span>
        <span className="mx-1">|</span>
        <span>{displayPage}</span>
      </p>
      <div className="flex flex-col items-center">
        {quote.organization?.logoUrl && (
          <img
            src={quote.organization.logoUrl}
            alt={`${quote.organization.name} logo`}
            className="h-8 w-auto object-contain"
          />
        )}
        <p className="text-[10px] font-bold text-gray-700 tracking-wider">
          -CONFIDENTIAL-
        </p>
      </div>
    </div>
  );
}

const TABLE_HEADER_CELL_CLASS =
  "border border-gray-400 px-2 py-1.5 text-[10px] font-bold tracking-wide text-gray-900 text-center";
const TABLE_CELL_CLASS =
  "border border-gray-400 px-2 py-1.5 text-xs text-gray-900 align-middle";
const COMMERCIAL_TABLE_CLASS = "w-full border-collapse table-fixed text-xs";

/**
 * Builds the pricing-table colgroup, widening to fit a Lead Time column when
 * needed. Widths sum the same in both variants so layout is unchanged when no
 * line has a lead time.
 * @param hasLeadTime - Whether a trailing Lead Time column is present
 * @returns The colgroup element for the commercial pricing table
 */
function buildCommercialColgroup(hasLeadTime: boolean) {
  if (!hasLeadTime) {
    return (
      <colgroup>
        <col style={{ width: "7%" }} />
        <col style={{ width: "43%" }} />
        <col style={{ width: "8%" }} />
        <col style={{ width: "17%" }} />
        <col style={{ width: "17%" }} />
      </colgroup>
    );
  }
  return (
    <colgroup>
      <col style={{ width: "7%" }} />
      <col style={{ width: "33%" }} />
      <col style={{ width: "8%" }} />
      <col style={{ width: "16%" }} />
      <col style={{ width: "16%" }} />
      <col style={{ width: "12%" }} />
    </colgroup>
  );
}

/**
 * Renders table rows for a single ITEM line, handling multiple quantity breaks.
 * @param line - The hydrated item line
 * @param index - Index within commercial lines
 * @param lineNumber - Display line number
 * @param quote - The hydrated quote data (for display-currency formatting)
 * @param hasLeadTime - Whether the table renders a trailing Lead Time column
 * @returns Table row(s) for the item
 */
function renderItemRows(
  line: PublicQuoteItemLine,
  index: number,
  lineNumber: number | null,
  quote: PublicQuote,
  hasLeadTime: boolean,
) {
  const itemDescription = cleanCustomerFacingItemDescription(
    line.description,
    line.item?.name,
  );
  const displayNumber = resolveLineDisplayNumber(line) ?? lineNumber;
  const leadTime = resolveLineLeadTime(line);
  const estimates = formatItemCostEstimates(line, quote);

  if (estimates.length === 0) {
    return (
      <tr key={`item-${index}`}>
        <td className={`${TABLE_CELL_CLASS} text-center`}>{displayNumber}</td>
        <td className={`${TABLE_CELL_CLASS} whitespace-pre-line`}>
          {itemDescription}
        </td>
        <td className={`${TABLE_CELL_CLASS} text-center`}>-</td>
        <td className={`${TABLE_CELL_CLASS} text-right`}>-</td>
        <td className={`${TABLE_CELL_CLASS} text-right`}>-</td>
        {hasLeadTime && (
          <td className={`${TABLE_CELL_CLASS} text-center`}>{leadTime}</td>
        )}
      </tr>
    );
  }

  if (estimates.length === 1) {
    const est = estimates[0];
    return (
      <tr key={`item-${index}`}>
        <td className={`${TABLE_CELL_CLASS} text-center`}>{displayNumber}</td>
        <td className={`${TABLE_CELL_CLASS} whitespace-pre-line`}>
          {itemDescription}
        </td>
        <td className={`${TABLE_CELL_CLASS} text-center`}>{est.quantity}</td>
        <td className={`${TABLE_CELL_CLASS} text-right whitespace-nowrap`}>
          {est.unitPrice}
        </td>
        <td
          className={`${TABLE_CELL_CLASS} text-right font-semibold whitespace-nowrap`}
        >
          {est.totalPrice}
        </td>
        {hasLeadTime && (
          <td className={`${TABLE_CELL_CLASS} text-center`}>{leadTime}</td>
        )}
      </tr>
    );
  }

  return estimates.map((est, qbIndex) => (
    <tr key={`item-${index}-qb-${qbIndex}`}>
      {qbIndex === 0 && (
        <>
          <td
            className={`${TABLE_CELL_CLASS} text-center`}
            rowSpan={estimates.length}
          >
            {displayNumber}
          </td>
          <td
            className={`${TABLE_CELL_CLASS} whitespace-pre-line`}
            rowSpan={estimates.length}
          >
            {itemDescription}
          </td>
        </>
      )}
      <td className={`${TABLE_CELL_CLASS} text-center`}>{est.quantity}</td>
      <td className={`${TABLE_CELL_CLASS} text-right whitespace-nowrap`}>
        {est.unitPrice}
      </td>
      <td
        className={`${TABLE_CELL_CLASS} text-right font-semibold whitespace-nowrap`}
      >
        {est.totalPrice}
      </td>
      {hasLeadTime && qbIndex === 0 && (
        <td
          className={`${TABLE_CELL_CLASS} text-center`}
          rowSpan={estimates.length}
        >
          {leadTime}
        </td>
      )}
    </tr>
  ));
}
